import { Milliseconds, SDKConfigurationType } from 'src/types';
import { ClientSettingsType } from './types';
import { Environment } from 'src/sdkInfoTypes';
import { KameleoonLogger } from 'src/logging';
import { Validator } from 'src/utilities';

export interface IClientSettings {
  readonly settings: ClientSettingsType;
}

export class ClientSettings implements IClientSettings {
  private updateInterval: number;
  private environment: string;
  private networkDomain: string | null;
  private requestTimeout: number;
  private siteCode: string;
  private cleanupInterval: number | null;
  private trackRetryDelay: number;
  private trackingInterval: number;

  constructor(siteCode: string, configuration?: Partial<SDKConfigurationType>) {
    KameleoonLogger.debug`CALL: new ClientSettings(siteCode: ${siteCode}, configuration: ${configuration})`;
    this.siteCode = siteCode;

    this.environment = this.getEnvironment(configuration);
    this.networkDomain = this.getNetworkDomain(configuration);
    this.updateInterval = this.getUpdateInterval(configuration);
    this.cleanupInterval = this.getCleanupInterval(configuration);
    this.requestTimeout = this.getRequestTimeout(configuration);
    this.trackingInterval = this.getTrackingInterval(configuration);

    this.trackRetryDelay = 5 * Milliseconds.Second;
    KameleoonLogger.debug`RETURN: new ClientSettings(siteCode: ${siteCode}, configuration: ${configuration})`;
  }

  private getTrackingInterval(
    configuration?: Partial<SDKConfigurationType>,
  ): number {
    if (typeof configuration?.trackingInterval === 'number') {
      if (configuration.trackingInterval < 100) {
        KameleoonLogger.warning(
          'Tracking interval must not be shorter than 100 ms. Minimum possible interval was applied.',
        );
        return 100;
      }

      if (configuration.trackingInterval > 1_000) {
        KameleoonLogger.warning(
          'Tracking interval must not be longer than 1000 ms. Maximum possible interval was applied.',
        );
        return 1_000;
      }

      return configuration.trackingInterval;
    }

    return 1_000;
  }

  private getEnvironment(
    configuration?: Partial<SDKConfigurationType>,
  ): string {
    if (configuration?.environment) {
      return configuration.environment;
    }

    return Environment.Production;
  }

  private getCleanupInterval(
    configuration?: Partial<SDKConfigurationType>,
  ): number | null {
    if (typeof configuration?.targetingDataCleanupInterval === 'number') {
      if (configuration.targetingDataCleanupInterval < 1) {
        KameleoonLogger.warning(
          'Targeting data cleanup interval must not be shorter than 1 minute. Minimum possible interval was applied.',
        );
        return 1 * Milliseconds.Minute;
      }

      return configuration.targetingDataCleanupInterval * Milliseconds.Minute;
    }

    return null;
  }

  private getUpdateInterval(
    configuration?: Partial<SDKConfigurationType>,
  ): number {
    if (typeof configuration?.updateInterval === 'number') {
      if (configuration.updateInterval < 1) {
        KameleoonLogger.warning(
          'Update interval must not be shorter than 1 minute. Minimum possible interval was applied.',
        );
        return 1 * Milliseconds.Minute;
      }

      return configuration.updateInterval * Milliseconds.Minute;
    }

    return 60 * Milliseconds.Minute;
  }

  private getNetworkDomain(
    configuration?: Partial<SDKConfigurationType>,
  ): string | null {
    const result = Validator.validateNetworkDomain(configuration?.networkDomain);
    if (!result.ok) {
      return null;
    }

    return result.data;
  }

  private getRequestTimeout(
    configuration?: Partial<SDKConfigurationType>,
  ): number {
    if (typeof configuration?.requestTimeout === 'number') {
      return configuration.requestTimeout;
    }

    return 10 * Milliseconds.Second;
  }

  get settings(): ClientSettingsType {
    return {
      trackingInterval: this.trackingInterval,
      trackRetryDelay: this.trackRetryDelay,
      cleanupInterval: this.cleanupInterval,
      updateInterval: this.updateInterval,
      requestTimeout: this.requestTimeout,
      networkDomain: this.networkDomain,
      environment: this.environment,
      siteCode: this.siteCode,
    };
  }
}
